import {
  EntityList,
  EntityListProps,
  renderDate,
  renderEnum,
  renderNewMailIcon,
  renderStatusIcon,
} from 'components/EntityList'
import { Proving, User, UserRole } from 'gql.generated'
import { Box } from 'grid'
import { useAuthContext } from 'hooks/useAuthContext'
import { ConstrainMode, SelectionMode } from 'office-ui-fabric-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { createFieldColumn } from 'views/helpers'

type ReadProvings_workers = any

export type ProvingDateListProps = EntityListProps<ReadProvings_workers>

export const ProvingDateList: React.FunctionComponent<ProvingDateListProps> =
  React.memo(
    ({ items, ...props }) => {
      const { t } = useTranslation()
      const auth = useAuthContext()
      const isAdmin = auth.hasUserRole(UserRole.SystemAdministrator)
      const isResourceManager = auth.hasUserRole(UserRole.ResourceManager)
      const isProvingOrganisation = auth.hasUserRole(
        UserRole.ProvingOrganisation
      )
      const isHrg = auth.hasUserRole(UserRole.Hrg)
      const isServiceTeam = auth.hasUserRole(UserRole.ServiceTeam)

      const canSeeNewMailsColumn = isAdmin
        ? true
        : isResourceManager
        ? true
        : isProvingOrganisation
        ? true
        : isHrg
        ? true
        : isServiceTeam
        ? true
        : false

      const fieldColumn = React.useMemo(() => createFieldColumn(t), [t])
      const columns = [
        fieldColumn('status', {
          onRender: renderStatusIcon,
          getGroupValue: (item: Proving) => t(`enum.status.${item.status}`),
          minWidth: 40,
          maxWidth: 60,
          ungroupable: true,
        }),
        fieldColumn('dateFrom', {
          onRender: renderDate,

          isSorted: true,
          ungroupable: true,
        }),
        fieldColumn('dateTo', {
          onRender: renderDate,
          ungroupable: true,
        }),
        fieldColumn('id', { ungroupable: true }),

        fieldColumn('title', {
          ungroupable: true,
          minWidth: 320,
        }),
        fieldColumn('provingGround.title', {
          onRender: (item, i, col) => {
            return `${item.provingGround!.title}, ${
              item.provingGround!.countryCode
            }`
          },
          isGrouped: true,
          getGroupValue: (item: Proving) =>
            `${item.provingGround!.title}, ${item.provingGround!.countryCode}`,
        }),
        fieldColumn('responsible', {
          onRender: (item, i, col) => {
            return (
              <div key={i}>
                {item.responsible.map((user: User, a: any) => {
                  return (
                    <Box key={a} style={{ whiteSpace: 'pre-line' }}>
                      {user.name}
                    </Box>
                  )
                })}
              </div>
            )
          },
          getGroupValue: (item: Proving) => {
            let result = ''
            if (item.responsible) {
              item.responsible.forEach((user: User) => {
                result = `${result} ${user.name}`
              })
            }
            return result
          },
          ungroupable: true,
          minWidth: 105,
        }),
        fieldColumn('department', { ungroupable: true }),
        fieldColumn('status', {
          onRender: (item, i, col) => {
            if (item.hasIncompleteBookings) {
              return `${renderEnum(t, 'status')(
                item,
                i,
                col
              )} (Buchungen teilweise in Bearbeitung)`
            } else {
              return renderEnum(t, 'status')(item, i, col)
            }
          },
          getGroupValue: (item: Proving) => t(`enum.status.${item.status}`),
          minWidth: 240,
          ungroupable: true,
        }),
      ]

      const newMailColumn = fieldColumn('newMails', {
        onRender: renderNewMailIcon,
        getGroupValue: (item: Proving) => {
          return item.newMails && item.newMails > 0
            ? 'Erprobungen mit neuen Mails'
            : 'Erprobungen ohne Neuigkeiten'
        },
      })

      if (canSeeNewMailsColumn) {
        columns.push(newMailColumn)
      }

      return (
        <EntityList
          alternate={true}
          stickyHeader={true}
          selectionMode={SelectionMode.single}
          items={items}
          constrainMode={ConstrainMode.horizontalConstrained}
          columns={columns}
          sortHighestGroup={true}
          {...props}
        />
      )
    },
    (prevProps, nextProps) =>
      prevProps.items.length === nextProps.items.length ? true : false
  )
